// @flow
import * as React from "react";
import styled from "styled-components";
import { parse } from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.min.json";
import countries from "react-phone-number-input/build/country names";
import "react-virtualized/styles.css";
import _Flag from "../../common/Flag";
import Gettext, { type WithGettext } from "../../common/Gettext";
import CountrySelect from "../CountrySelect";
import Input from "./Input";

const Flag = styled(_Flag)`
  vertical-align: middle;
`;

const Unbreakable = styled.span`
  white-space: nowrap;
`;

const WORLD_OPTION = {
  value: <Gettext>Other</Gettext>,
  label: <Gettext>Other</Gettext>,
  phoneCode: "",
  phoneNumber: "",
  code: "world"
};
const COUNTRY_LIST = countries
  .map(([countryCode, countryName]) => {
    const [phoneCode] = metadata.countries[countryCode.toUpperCase()] || [];
    if (phoneCode) {
      return {
        value: countryName,
        label: countryName,
        phoneCode,
        code: countryCode
      };
    }

    return null;
  })
  .filter(item => item !== null);

COUNTRY_LIST.push(WORLD_OPTION);

const InputContainer = styled.div`
  flex: 1;
`;

const DisplayMode = styled.div`
  vertical-align: middle;
`;

const CodePart = styled.span`
  margin-right: 4px;
  color: #aaa;
  vertical-align: middle;
`;

const PhonePart = styled.span`
  color: #4e2a81;
  vertical-align: middle;
`;

export function checkLeadingZero(phoneNumber: string) {
  return /^\+?0\d*$/.test(phoneNumber);
}

export function getLeadingZeroError(phoneNumber: string, phoneExample: string) {
  if (checkLeadingZero(phoneNumber)) {
    return phoneExample ? (
      <React.Fragment>
        <Gettext>
          Incorrect number! It seems like you type your local number. Please,
          use international format, see an example:
        </Gettext>{" "}
        <Unbreakable>{phoneExample}</Unbreakable>
      </React.Fragment>
    ) : (
      <Gettext>
        Incorrect number! It seems like you type your local number. Please, use
        international format.
      </Gettext>
    );
  }

  return "";
}

export function ripOffLeadingZero(phoneCode: number, phoneNumber: string) {
  let number = phoneNumber;
  const r = new RegExp(`^\\+${phoneCode}0(\\d*)$`);
  const x = r.exec(number);
  if (x && x[1]) {
    number = "+" + phoneCode + x[1];
  }

  return number;
}

export function ripOffDuplicatedCountryCode(
  phoneCode: number,
  phoneNumber: string
) {
  const PLUS_LENGTH = 1;
  const MAX_NUMBER_LENGTH = 11; // Due to https://jgit.me/binary-options/irontrade-web/issues/158

  let number = phoneNumber;
  const phoneCodeLength = (phoneCode || "").toString().length;
  if (
    phoneCode &&
    number.length > MAX_NUMBER_LENGTH + phoneCodeLength + PLUS_LENGTH
  ) {
    const r = new RegExp(`^\\+${phoneCode}${phoneCode}(0?)\\d*$`);
    const x = r.exec(number);
    if (x) {
      let l = PLUS_LENGTH + 2 * phoneCodeLength; // double of phone code size (actual code + duplicate code)
      if (x[1]) {
        l += 1; // Increase length for national "0"
      }

      number = "+" + phoneCode + number.slice(l); // rewrite number without duplicated country code
    }
  }

  return number;
}

type HintProps = {
  countryCode?: string,
  phoneExample?: string
};

export const PhoneInputHint = (props: HintProps) => {
  if (props.hint) return props.hint;

  if (props.countryCode === "world") {
    return <Gettext>We recommend you to choose a country code.</Gettext>;
  }

  if (props.phoneExample) {
    // Use wrapping span so the text wouldn't be splitted by flex in profile
    return (
      <span>
        <Gettext>
          Please, choose your country code and enter your local number without 0
          at the beginning. Example:
        </Gettext>
        <Unbreakable> {props.phoneExample}</Unbreakable>
      </span>
    );
  }

  return (
    <Gettext>
      Please choose your country and enter your phone number in international
      format.
    </Gettext>
  );
};

export function parsePhone(phone: string | number, defaultCountry: string) {
  let parsedAsIs = parse(phone);
  let parsedPlus = parse(`+${phone}`);

  let parsed = parsedAsIs;
  let country;
  let value = "";

  if (parsedPlus && parsedPlus.country) {
    parsed = parsedPlus;
  }

  if (parsed && parsed.country) {
    value = parsed.phone;
    country = COUNTRY_LIST.find(
      country => country.code.toUpperCase() === parsed.country.toUpperCase()
    );
  } else if (defaultCountry) {
    country = COUNTRY_LIST.find(
      country => country.code.toUpperCase() === defaultCountry.toUpperCase()
    );
  }

  if (!country) {
    country = WORLD_OPTION;
  }

  return {
    value,
    country
  };
}

type CountryOption = {
  code: string,
  label: string,
  phoneCode: number,
  value: string
};

type Props = {
  Container?: React$ElementType,
  defaultCountry: string,
  lockCountry?: boolean,
  errorMessage: ?string,
  placeholder: string | WithGettext,
  onChange: Function,
  onBlur?: Function,
  focus?: boolean,
  id: string,
  value: string,
  showPhoneInfo: boolean,
  showInfo?: boolean,
  onFocus?: boolean,
  isProfile: boolean,
  displayMode?: boolean
};

type State = {
  countryAutoSwitched: boolean,
  country: CountryOption,
  value: string
};

class PhoneInput extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    let { value, country } = parsePhone(props.value, this.props.defaultCountry);

    if (!value) {
      value = props.value;
    }

    if (country && this.checkPlus(value)) {
      value = value.slice(1 + country.phoneCode.length);
    }

    this.state = {
      countryAutoSwitched: false,
      country,
      value: value
    };
  }

  checkPlus = (value: string) => (value.charAt(0) === "+" ? 1 : 0);

  selectChangeHandler = (e: {
    target: { name?: any, value: CountryOption }
  }) => {
    let value = this.state.value;
    const country = e.target.value;
    const code = country.phoneCode;

    if (code && this.checkPlus(value)) {
      value = value.slice(1);
    }

    if (!code && !this.checkPlus(value)) {
      value = `+${value}`;
    }

    this.setState(
      {
        selectChanged: true,
        countryAutoSwitched: false,
        value,
        country
      },
      this.onChange
    );
  };

  inputChangeHandler = (e: SyntheticEvent<HTMLInputElement>) => {
    const regular = /^\+?\d*$/;
    const unplusable = /^\d*$/;

    let r = unplusable;
    let r1 = e.currentTarget.value;
    let r2 = this.state.value;

    let countryAutoSwitched = false;
    let country = this.state.country;
    if (country === WORLD_OPTION) {
      r = regular;
      r1 = this.checkPlus(e.currentTarget.value)
        ? e.currentTarget.value
        : "+" + e.currentTarget.value;
    }

    let value = r.test(e.currentTarget.value) ? r1 : r2;

    if (this.checkPlus(value)) {
      // If user puts the plus sign in then try to re-parse input value
      const parsed = parse(value);
      if (parsed && parsed.country) {
        value = parsed.phone;
        country = COUNTRY_LIST.find(
          country => country.code.toUpperCase() === parsed.country.toUpperCase()
        );

        if (country) {
          countryAutoSwitched = true;
        }
      }
    }

    this.setState(
      {
        selectChanged: false,
        countryAutoSwitched,
        value,
        country
      },
      this.onChange
    );
  };

  onChange = () => {
    const { selectChanged, countryAutoSwitched, country, value } = this.state;
    if (country.phoneCode !== undefined) {
      const sign = country.phoneCode ? "+" : "";
      const phone = value ? `${sign}${country.phoneCode}${value}` : "";
      this.props.onChange({
        selectChanged,
        countryAutoSwitched,
        number: value,
        country,
        fullNumber: phone
      });
    }
  };

  render() {
    const {
      Container = React.Fragment,
      lockCountry,
      errorMessage,
      placeholder,
      focus,
      id,
      onBlur,
      onFocus,
      showInfo,
      displayMode,
      isProfile,
      showPhoneInfo,
      version,
      isWithdraw
    } = this.props;

    const blurHandler = typeof onBlur === "function" ? onBlur : null;
    const focusHandler = typeof onFocus === "function" ? onFocus : null;
    const selectError =
      this.state.country.phoneCode === undefined && errorMessage;
    const inputError =
      this.state.country.phoneCode !== undefined && errorMessage;

    if (displayMode) {
      let value = this.state.value || this.props.value;

      return (
        <Container>
          <DisplayMode>
            {this.state.country.phoneCode && (
              <React.Fragment>
                <Flag country={this.state.country.code} />
                <CodePart>+{this.state.country.phoneCode}</CodePart>
              </React.Fragment>
            )}

            <PhonePart>{value}</PhonePart>
          </DisplayMode>
        </Container>
      );
    }

    return (
      <Container>
        <div>
          <CountrySelect
            value={this.state.country}
            options={
              this.props.countries
                ? COUNTRY_LIST.filter(item =>
                    this.props.countries.includes(item.code)
                  )
                : COUNTRY_LIST
            }
            onChange={lockCountry ? null : this.selectChangeHandler}
            invalid={selectError}
            error={!!inputError}
            showInfo={showInfo}
            isProfile={isProfile}
            version={version}
            isWithdraw={isWithdraw}
          />
        </div>

        <InputContainer>
          <Input
            id={id || "phone-input"}
            focus={focus}
            type="tel"
            placeholder={placeholder}
            value={this.state.value}
            label=""
            error={inputError}
            onChange={this.inputChangeHandler}
            onFocus={focusHandler}
            onBlur={blurHandler}
            wide={true}
            isProfile={isProfile}
            isWithdraw={isWithdraw}
            showPhoneInfo={showPhoneInfo}
            version={version}
            autocomplete="tel"
          />
        </InputContainer>
      </Container>
    );
  }
}

export default PhoneInput;



// WEBPACK FOOTER //
// ./src/components/ReVerification/Phone/PhoneInput.js